'use client'

import { FC, useState, useRef } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { ArrowLeft } from 'lucide-react'
import { Button } from '@repo/ui/button'
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@repo/ui/input-otp'
import { FormField, FormItem, FormControl, FormMessage } from '@repo/ui/form'
import { useFormError } from '@/hooks/useFormError'
import { AuthResult } from '@/types'

type CodeFormData = {
  code: string
}

type AuthMode = 'signin' | 'signup'

type Props = {
  form: UseFormReturn<CodeFormData>
  email: string
  onCodeSubmit: (data: CodeFormData) => Promise<AuthResult | null>
  onResend: () => Promise<AuthResult | null>
  onBack: () => void
  authMode: AuthMode
}

const CodeForm: FC<Props> = ({ form, email, onCodeSubmit, onResend, onBack, authMode }) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const { error, handleAsyncOperation, clearError } = useFormError()
  const inputRef = useRef<HTMLInputElement>(null)

  const {
    setValue,
    clearErrors,
    formState: { errors, isSubmitting: formIsSubmitting },
  } = form

  // Handle code change and auto-submit when complete
  async function handleCodeChange(value: string) {
    setValue('code', value)
    clearError()
    clearErrors()

    // Auto-submit when all 6 digits are entered
    if (value.length === 6) {
      form.handleSubmit(handleSubmit, () => {
        setValue('code', '')
        setTimeout(() => inputRef.current?.focus(), 0)
      })()
    }
  }

  // Handle form submission with proper validation
  const handleSubmit = async (data: CodeFormData) => {
    setIsSubmitting(true)
    clearErrors()

    await handleAsyncOperation(
      () => onCodeSubmit(data),
      'Please try again.',
      undefined,
      () => {
        setValue('code', '')
        setTimeout(() => inputRef.current?.focus(), 0)
      }
    )
    setIsSubmitting(false)
  }

  const handleResend = async () => {
    setIsResending(true)

    setValue('code', '')
    clearError()
    clearErrors()

    await handleAsyncOperation(onResend, 'Please try again.')

    setIsResending(false)
  }

  const isFormLoading = isSubmitting || formIsSubmitting || isResending

  return (
    <form onSubmit={form.handleSubmit(handleSubmit)} className="mt-10 w-full text-center">
      <div className="text-base font-regular text-grey-600 text-center flex flex-col">
        <span>
          {authMode === 'signin'
            ? 'We sent a verification code to'
            : 'We sent a verification code to complete your sign up at'}
        </span>
        <span className="font-semibold text-grey-800">{email}</span>
      </div>

      {error && (
        <div className="p-3 mt-2 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <div className="mt-4 sm:mt-10">
        <FormField
          control={form.control}
          name="code"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <div className="flex justify-center">
                  <InputOTP
                    ref={inputRef}
                    maxLength={6}
                    value={field.value || ''}
                    onChange={(value) => {
                      field.onChange(value)
                      handleCodeChange(value)
                    }}
                    disabled={isFormLoading}
                    className="justify-center"
                  >
                    <InputOTPGroup>
                      {Array.from({ length: 6 }).map((_, index) => (
                        <InputOTPSlot
                          key={index}
                          index={index}
                          className={`h-12 w-12 text-lg font-semibold ${error || errors.code ? 'border-red-300 focus:border-red-300' : ''}`}
                        />
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                </div>
              </FormControl>

              <FormMessage className="text-sm text-red-600" />
            </FormItem>
          )}
        />

        {isFormLoading && <div className="mt-2 text-sm text-grey-600">Verifying...</div>}
      </div>

      <Button
        variant="ghost"
        onClick={handleResend}
        className="mt-20 sm:mt-10 text-sm h-9 font-regular text-grey-600"
        disabled={isFormLoading}
      >
        {isResending ? 'Resending...' : 'Resend code'}
      </Button>

      <Button
        variant="ghost"
        onClick={onBack}
        className="mx-auto mt-7 sm:mt-10 flex items-center gap-1 text-sm font-medium text-grey-600"
        disabled={isFormLoading}
      >
        <ArrowLeft size={16} />
        Go back
      </Button>
    </form>
  )
}

export default CodeForm
